import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiScaleBalance } from '@mdi/js'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { AppIcon } from '../ui/icon'
import { useLocale } from '../../contexts/LocaleContext'
import { useUpdateExpense } from '../../hooks/useQueries'
import { useExpenseClassification } from '../../hooks/useExpenseStatement'
import {
  percent,
  recoverableKey,
  useAcceptRecoverableProposals,
  type RecoverableProposal,
} from '../../hooks/useRecoverableCharges'

const SELECT_CLASS = 'h-9 rounded-md border border-input bg-background px-3 text-sm'

/** The proposal's value as a short phrase: "recoverable", "recoverable at 75 %". */
export function proposalPhrase(t: (k: string, o?: Record<string, unknown>) => string, p: RecoverableProposal): string {
  const key = recoverableKey(p.recoverable, p.recoverable_ratio)
  return t(`recoverable.proposal_${key}`, { percent: percent(p.recoverable_ratio) })
}

/**
 * Whether the tenant pays this line (spec 2026-10-25-09): the stored decision,
 * the read-time proposal with its legal basis, Accept when the line is
 * unqualified, and a decision by hand.
 */
export function ExpenseRecoverableCard({ org, expenseUid, canEdit }: { org: string; expenseUid: string; canEdit: boolean }) {
  const { t } = useTranslation('expenses')
  const { locale } = useLocale()
  const { data: c, refetch } = useExpenseClassification(org, expenseUid)
  const update = useUpdateExpense(org, expenseUid)
  const accept = useAcceptRecoverableProposals(org)
  const [deciding, setDeciding] = useState(false)
  const [choice, setChoice] = useState<'yes' | 'partial' | 'no'>('yes')
  const [ratio, setRatio] = useState('')

  if (!c) return null
  const p = c.recoverable_proposal
  const decided = c.recoverable != null
  const key = recoverableKey(c.recoverable, c.recoverable_ratio)
  const explanation = p?.rule?.explanation
  const explanationText = explanation ? (locale.startsWith('fr') ? explanation.fr || explanation.en : explanation.en || explanation.fr) : ''

  const doAccept = async () => {
    await accept.mutateAsync([expenseUid], p?.rule ? { [expenseUid]: p.rule.uid } : undefined)
    refetch()
  }

  const openDecide = () => {
    setChoice(key === 'unqualified' ? 'yes' : key)
    setRatio(c.recoverable_ratio != null ? String(percent(c.recoverable_ratio)) : '')
    setDeciding(true)
  }

  const save = async () => {
    const pct = Number(ratio)
    if (choice === 'partial' && !(pct > 0 && pct < 100)) return
    await update.mutateAsync({
      recoverable: choice !== 'no',
      recoverable_ratio: choice === 'partial' ? pct / 100 : null,
    })
    setDeciding(false)
    refetch()
  }

  return (
    <Card data-testid="expense-recoverable-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <AppIcon path={mdiScaleBalance} size="md" />
          {t('recoverable.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={decided ? 'font-medium text-foreground' : 'font-medium text-muted-foreground'}
            data-testid="expense-recoverable-value"
          >
            {t(`recoverable.value_${key}`, { percent: percent(c.recoverable_ratio) })}
          </span>
          {c.recoverable_rule_uid && (
            <Badge variant="outline" className="text-[11px] font-normal" data-testid="expense-recoverable-from-rule">
              {t('recoverable.from_rule')}
            </Badge>
          )}
          {p?.matches_decision === false && (
            <Badge variant="secondary" className="text-[11px] font-normal" data-testid="expense-recoverable-differs">
              {t('recoverable.differs')}
            </Badge>
          )}
        </div>

        {p?.status === 'proposed' ? (
          <div className="space-y-1 rounded-md border border-warning/25 bg-warning/10 p-3 text-sm" data-testid="expense-recoverable-proposal">
            <p>
              <span className="text-muted-foreground">{t('recoverable.proposal_label')}</span>{' '}
              <span className="font-medium">{proposalPhrase(t, p)}</span>
              {p.rule?.legal_basis && (
                <>
                  {' · '}
                  {p.rule.source_url ? (
                    <a href={p.rule.source_url} target="_blank" rel="noreferrer" className="underline" data-testid="expense-recoverable-basis">
                      {p.rule.legal_basis}
                    </a>
                  ) : (
                    <span data-testid="expense-recoverable-basis">{p.rule.legal_basis}</span>
                  )}
                </>
              )}
            </p>
            {p.rule?.origin === 'organization' && (
              <p className="text-xs text-muted-foreground">{t('recoverable.origin_organization')}{p.rule.note ? ` · ${p.rule.note}` : ''}</p>
            )}
            {explanationText && <p className="text-xs text-muted-foreground">{explanationText}</p>}
            {p.rule?.release && !p.rule.release.validated && (
              <p className="text-xs text-foreground">{t('recoverable.indicative')}</p>
            )}
            {!decided && canEdit && (
              <Button size="sm" className="mt-1" onClick={doAccept} disabled={accept.isPending} data-testid="expense-recoverable-accept">
                {t('recoverable.accept')}
              </Button>
            )}
          </div>
        ) : p ? (
          <p className="text-sm text-muted-foreground" data-testid="expense-recoverable-status">
            {t(`recoverable.status_${p.status}`, { jurisdiction: p.jurisdiction || '?' })}
          </p>
        ) : null}

        {canEdit && p?.status !== 'not_applicable' && (deciding ? (
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={SELECT_CLASS}
              value={choice}
              onChange={(e) => setChoice(e.target.value as typeof choice)}
              data-testid="expense-recoverable-select"
            >
              <option value="yes">{t('recoverable.value_yes')}</option>
              <option value="partial">{t('recoverable.value_partial', { percent: '…' })}</option>
              <option value="no">{t('recoverable.value_no')}</option>
            </select>
            {choice === 'partial' && (
              <Input
                type="number"
                min={1}
                max={99}
                className="w-24"
                aria-label={t('recoverable.ratio_label')}
                placeholder={t('recoverable.ratio_label')}
                value={ratio}
                onChange={(e) => setRatio(e.target.value)}
                data-testid="expense-recoverable-ratio"
              />
            )}
            <Button size="sm" onClick={save} disabled={update.isPending} data-testid="expense-recoverable-save">
              {t('recoverable.save')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDeciding(false)} data-testid="expense-recoverable-cancel">
              {t('recoverable.cancel')}
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={openDecide} data-testid="expense-recoverable-decide">
            {t('recoverable.decide')}
          </Button>
        ))}
      </CardContent>
    </Card>
  )
}
